import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { TrendingUp, Loader2, FileDown, FileSpreadsheet, Filter } from "lucide-react";
import api, { API } from "../lib/api";
import { formatFCFA, METHOD_LABELS, STATUS_LABELS } from "../lib/format";
import { PageHeader, Spinner } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const COLORS = ["#06b6d4", "#f59e0b", "#334155", "#059669", "#0d9488", "#f43f5e"];

export default function Reports() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [filters, setFilters] = useState({ start: "", end: "", customer_id: "all", establishment_id: "all", method: "all" });

  useEffect(() => {
    api.get("/reports/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/establishments").then((r) => setEstablishments(r.data)).catch(() => {});
  }, []);

  const runReminders = async () => {
    setRunning(true);
    try {
      await api.post("/reminders/run");
      toast.success("Analyse des echeances lancee — rappels envoyes aux clients concernes.");
    } catch (e) {
      toast.error("Impossible de lancer les rappels");
    } finally {
      setRunning(false);
    }
  };

  const exportReport = (format) => {
    const params = new URLSearchParams({ format });
    if (filters.start) params.append("start", filters.start);
    if (filters.end) params.append("end", filters.end);
    if (filters.customer_id !== "all") params.append("customer_id", filters.customer_id);
    if (filters.establishment_id !== "all") params.append("establishment_id", filters.establishment_id);
    if (filters.method !== "all") params.append("method", filters.method);
    window.open(`${API}/reports/export?${params.toString()}`, "_blank");
    toast.success(format === "pdf" ? "Export PDF genere" : "Export CSV genere");
  };

  const estOptions = filters.customer_id === "all"
    ? establishments
    : establishments.filter((e) => e.customer_id === filters.customer_id);

  if (!data) return <Spinner />;

  const methodData = data.by_method.map((m) => ({ name: METHOD_LABELS[m.method] || m.method, value: m.amount }));
  const statusData = data.invoice_status.map((s) => ({ name: STATUS_LABELS[s.status] || s.status, value: s.count }));

  return (
    <div>
      <PageHeader
        title="Rapports & relances"
        subtitle="Analyse des encaissements, exports filtres et declenchement des rappels d'echeance."
        actions={
          <Button data-testid="run-reminders-btn" onClick={runReminders} disabled={running} className="bg-teal-600 hover:bg-teal-700">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <TrendingUp className="h-4 w-4 mr-1.5" />}
            Lancer les rappels
          </Button>
        }
      />

      <Card className="p-5 mb-8">
        <div className="flex items-center gap-2 mb-4 text-slate-700">
          <Filter className="h-4 w-4 text-teal-500" />
          <h3 className="font-display font-semibold">Export du rapport d'encaissement</h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label className="text-xs">Date debut</Label>
            <Input data-testid="filter-start" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Date fin</Label>
            <Input data-testid="filter-end" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Groupe scolaire</Label>
            <Select value={filters.customer_id} onValueChange={(v) => setFilters({ ...filters, customer_id: v, establishment_id: "all" })}>
              <SelectTrigger data-testid="filter-customer" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white z-50 max-h-64">
                <SelectItem value="all">Tous les groupes</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Etablissement</Label>
            <Select value={filters.establishment_id} onValueChange={(v) => setFilters({ ...filters, establishment_id: v })}>
              <SelectTrigger data-testid="filter-establishment" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white z-50 max-h-64">
                <SelectItem value="all">Tous</SelectItem>
                {estOptions.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Moyen de paiement</Label>
            <Select value={filters.method} onValueChange={(v) => setFilters({ ...filters, method: v })}>
              <SelectTrigger data-testid="filter-method" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="wave">Wave</SelectItem>
                <SelectItem value="orange_money">Orange Money</SelectItem>
                <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
                <SelectItem value="cash">Especes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button data-testid="export-pdf-btn" onClick={() => exportReport("pdf")} variant="outline" className="border-slate-300">
            <FileDown className="h-4 w-4 mr-1.5" /> Export PDF
          </Button>
          <Button data-testid="export-csv-btn" onClick={() => exportReport("csv")} variant="outline" className="border-slate-300">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </Card>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card className="p-6"><p className="text-xs uppercase tracking-wider text-slate-500">Taux de recouvrement</p><p className="mt-1 font-mono text-3xl font-bold text-teal-600">{data.collection_rate}%</p></Card>
        <Card className="p-6"><p className="text-xs uppercase tracking-wider text-slate-500">Factures emises</p><p className="mt-1 font-mono text-3xl font-bold text-slate-900">{data.total_invoices}</p></Card>
        <Card className="p-6"><p className="text-xs uppercase tracking-wider text-slate-500">Paiements valides</p><p className="mt-1 font-mono text-3xl font-bold text-slate-900">{data.total_payments}</p></Card>
        <Card className="p-6"><p className="text-xs uppercase tracking-wider text-slate-500">Encaisse total</p><p className="mt-1 font-mono text-2xl font-bold text-emerald-600">{formatFCFA(data.by_method.reduce((a, b) => a + b.amount, 0))}</p></Card>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Encaissements par methode</h3>
          {methodData.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">Aucune donnee</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={methodData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={3}>
                  {methodData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatFCFA(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Statut des factures</h3>
          {statusData.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">Aucune donnee</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}
